/* global console */

import * as React from "react";
import { makeStyles, tokens } from "@fluentui/react-components";
import Markdown from "markdown-to-jsx";
import { parseAnswerIntoSegments, stripImageMarkers } from "@/utils/image-markers";
import { AnswerImage } from "@/types";

const { useMemo } = React;

interface AnswerWithImagesProps {
  answer: string;
  images?: AnswerImage[];
  className?: string;
  markdownOptions?: object;
}

/**
 * Renders answer text with embedded images.
 * Parses [[IMG:n]] markers and replaces them with actual images.
 */
export const AnswerWithImages: React.FC<AnswerWithImagesProps> = ({
  answer,
  images = [],
  className,
  markdownOptions = {},
}) => {
  const styles = useStyles();

  // Build a map of marker index to image for quick lookup
  const imageMap = useMemo(() => {
    const map = new Map<number, AnswerImage>();
    for (const img of images) {
      map.set(img.markerIndex, img);
    }
    return map;
  }, [images]);

  // Parse answer into segments
  const segments = useMemo(() => parseAnswerIntoSegments(answer), [answer]);

  // If no images or no markers found, render as plain markdown
  const hasImageMarkers = segments.some((s) => s.type === "image");
  if (images.length === 0 || !hasImageMarkers) {
    const cleanAnswer = stripImageMarkers(answer);
    return (
      <Markdown className={className} options={markdownOptions}>
        {cleanAnswer}
      </Markdown>
    );
  }

  return (
    <div className={className}>
      {segments.map((segment, idx) => {
        if (segment.type === "text") {
          return (
            <Markdown key={idx} options={markdownOptions}>
              {segment.content}
            </Markdown>
          );
        }

        // Image segment
        const image = imageMap.get(segment.index);
        if (!image) {
          return (
            <span key={idx} className={styles.imagePlaceholder}>
              [Image not found]
            </span>
          );
        }

        // Use SAS URL directly - no auth headers needed
        const imageUrl = image.documentImage.url;
        if (!imageUrl) {
          return (
            <span key={idx} className={styles.imagePlaceholder}>
              [Image URL not available]
            </span>
          );
        }

        return (
          <div key={idx} className={styles.imageContainer}>
            <img
              src={imageUrl}
              alt={image.documentImage.description || "Answer image"}
              className={styles.image}
              onError={(e) => {
                console.error("Image failed to load:", imageUrl);
                (e.target as HTMLImageElement).style.display = "none";
              }}
            />
          </div>
        );
      })}
    </div>
  );
};

const useStyles = makeStyles({
  imageContainer: {
    marginTop: "8px",
    marginBottom: "8px",
  },
  image: {
    maxWidth: "100%",
    height: "auto",
    borderRadius: "4px",
    border: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  imagePlaceholder: {
    fontSize: "11px",
    color: tokens.colorNeutralForeground3,
    fontStyle: "italic",
  },
});

export default AnswerWithImages;
